import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

interface InformationItem {
  startAt: string;
  endAt: string;
  title: string;
  body: string;
}

interface InformationFile {
  informations?: InformationItem[];
}

/** 表示開始/終了日時（クライアント時間）の範囲内にあるアイテムのみを抽出する */
const filterVisibleItems = (items: InformationItem[], now: Date): InformationItem[] => {
  return items.filter((item) => {
    if (!item || !item.title) return false;
    const start = item.startAt ? new Date(item.startAt) : null;
    const end = item.endAt ? new Date(item.endAt) : null;
    if (start && (isNaN(start.getTime()) || now < start)) return false;
    if (end && (isNaN(end.getTime()) || now > end)) return false;
    return true;
  });
};

/**
 * ログインページの告知エリア。
 * /information.json から告知を取得し、表示期間内のアイテムをタイトル・本文で表示する。
 * 表示できるアイテムがない場合は何も描画しない。
 */
const LoginAnnouncement: React.FC = () => {
  const theme = useTheme();
  const [items, setItems] = useState<InformationItem[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadInformation = async () => {
      try {
        const response = await fetch('/information.json', { cache: 'no-store' });
        if (!response.ok) return;

        const data: InformationFile = await response.json();
        if (cancelled) return;

        const informations = Array.isArray(data?.informations) ? data.informations : [];
        setItems(filterVisibleItems(informations, new Date()));
      } catch (error) {
        // 告知の取得失敗はログインの妨げにしない（エリアごと非表示）
        console.error('告知情報の読み込みに失敗しました:', error);
      }
    };

    loadInformation();

    return () => {
      cancelled = true;
    };
  }, []);

  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      className="login-announcement"
      sx={{
        width: '450px',
        maxWidth: '100%',
        mb: 3,
        px: { xs: 2, sm: 0 },
      }}
    >
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: `1px solid ${theme.palette.smartSkillTalk.orange}`,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: theme.palette.smartSkillTalk.orange,
            color: '#FFFFFF',
          }}
        >
          <CampaignOutlinedIcon fontSize="small" />
          <Typography component="h2" sx={{ fontSize: '15px', fontWeight: 600, letterSpacing: '0.08em' }}>
            お知らせ
          </Typography>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          {items.map((item, index) => (
            <Box
              key={`${item.title}-${index}`}
              sx={{
                pt: index === 0 ? 0 : 1.5,
                mt: index === 0 ? 0 : 1.5,
                borderTop: index === 0 ? 'none' : `1px solid ${theme.palette.smartSkillTalk.lightBlue}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.palette.smartSkillTalk.textDark,
                  mb: 0.5,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.7,
                  color: theme.palette.smartSkillTalk.textDark,
                  whiteSpace: 'pre-line',
                }}
              >
                {item.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginAnnouncement;
